import { c as _c } from "react/compiler-runtime";
import { postEntitiesByFpIdSentilinkBySignalIdOptions } from '@onefootprint/fetch-dashboard';
import type { RiskSignal } from '@onefootprint/request-types/dashboard';
import { Button, Dialog, Divider, Popover } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { format } from 'date-fns';
import capitalize from 'lodash/capitalize';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEntityId from '@/components/entities/components/details/hooks/use-entity-id';
import AmlMatches from './components/aml-matches';
import SentilinkContent from './components/sentilink-content';
import useRiskSignalSeverityText from './hooks/use-risk-signal-severity-text';
type RiskSignalItemProps = {
  riskSignal: RiskSignal;
};
type SeverityColor = 'error' | 'warning' | 'info' | 'neutral';
type DialogType = 'aml' | 'sentilink' | null;
const RiskSignalItem = t0 => {
  const $ = _c(84);
  const {
    riskSignal
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings.riskSignals"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const riskSignalSeverityT = useRiskSignalSeverityText();
  const entityId = useEntityId();
  const [activeDialog, setActiveDialog] = useState(null);
  const {
    id,
    reasonCode,
    severity,
    dataSource,
    description,
    note,
    timestamp
  } = riskSignal;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsPopoverOpen(false);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleEscapeKeyDown = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsPopoverOpen(false);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handlePointerDownOutside = t3;
  let t4;
  if ($[3] !== entityId || $[4] !== id) {
    t4 = postEntitiesByFpIdSentilinkBySignalIdOptions({
      path: {
        fpId: entityId,
        signalId: id
      }
    });
    $[3] = entityId;
    $[4] = id;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== riskSignal.hasSentilinkDetail || $[7] !== t4) {
    t5 = {
      ...t4,
      enabled: riskSignal.hasSentilinkDetail
    };
    $[6] = riskSignal.hasSentilinkDetail;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: sentilinkData
  } = useQuery(t5);
  const getSeverityColor = _temp;
  let t6;
  if ($[9] !== severity) {
    t6 = getSeverityColor(severity);
    $[9] = severity;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const severityColor = t6;
  let t7;
  if ($[11] !== t) {
    t7 = vendor => {
      const vendorKey = vendor.toLowerCase();
      const vendorMap = {
        footprint: () => t(_temp2),
        idology: () => t(_temp3),
        lexis: () => t(_temp4),
        experian: () => t(_temp5),
        twilio: () => t(_temp6),
        middesk: () => t(_temp7),
        incode: () => t(_temp8),
        stytch: () => t(_temp9),
        neuro_id: () => t(_temp10),
        samba_safety: () => t(_temp11),
        sentilink: () => t(_temp12),
        comply_advantage: () => t(_temp13)
      };
      return vendorMap[vendorKey]?.() || vendor;
    };
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const getVendorName = t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = _temp14;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let dialogConfig;
  let handleCloseDialog;
  let handleViewDetails;
  let t10;
  let t9;
  if ($[14] !== dataSource || $[15] !== description || $[16] !== getVendorName || $[17] !== id || $[18] !== note || $[19] !== reasonCode || $[20] !== riskSignal.hasAmlHits || $[21] !== riskSignal.hasSentilinkDetail || $[22] !== sentilinkData || $[23] !== severity || $[24] !== severityColor || $[25] !== t || $[26] !== timestamp) {
    const attributesInDetails = [{
      label: t(t8),
      value: description,
      className: "sm:col-span-2 text-body-2 max-w-[60ch]"
    }, {
      label: t(_temp15),
      value: note,
      className: "sm:col-span-2 text-body-2 max-w-[60ch]"
    }, {
      label: t(_temp16),
      value: capitalize(getVendorName(dataSource)),
      className: "text-body-2"
    }, {
      label: t(_temp17),
      value: reasonCode,
      className: "text-snippet-2"
    }, {
      label: t(_temp18),
      value: capitalize(severity),
      className: `text-${severityColor}`
    }, {
      label: t(_temp19),
      value: format(new Date(timestamp), "MM/dd/yyyy HH:mm"),
      className: "text-snippet-2"
    }];
    let t11;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = type => {
        setActiveDialog(type);
      };
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    handleViewDetails = t11;
    let t12;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = () => {
        setActiveDialog(null);
      };
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    handleCloseDialog = t12;
    let t13;
    bb0: {
      if (riskSignal.hasSentilinkDetail) {
        let t14;
        if ($[34] !== t) {
          t14 = t(_temp20);
          $[34] = t;
          $[35] = t14;
        } else {
          t14 = $[35];
        }
        let t15;
        if ($[36] !== t) {
          t15 = t(_temp21);
          $[36] = t;
          $[37] = t15;
        } else {
          t15 = $[37];
        }
        let t16;
        if ($[38] !== sentilinkData) {
          t16 = <SentilinkContent data={sentilinkData} />;
          $[38] = sentilinkData;
          $[39] = t16;
        } else {
          t16 = $[39];
        }
        let t17;
        if ($[40] !== t14 || $[41] !== t15 || $[42] !== t16) {
          t17 = {
            type: "sentilink" as DialogType,
            label: t14,
            title: t15,
            content: t16
          };
          $[40] = t14;
          $[41] = t15;
          $[42] = t16;
          $[43] = t17;
        } else {
          t17 = $[43];
        }
        t13 = t17;
        break bb0;
      }
      if (riskSignal.hasAmlHits) {
        let t14;
        if ($[44] !== t) {
          t14 = t(_temp22);
          $[44] = t;
          $[45] = t14;
        } else {
          t14 = $[45];
        }
        let t15;
        if ($[46] !== t) {
          t15 = t(_temp23);
          $[46] = t;
          $[47] = t15;
        } else {
          t15 = $[47];
        }
        let t16;
        if ($[48] !== id) {
          t16 = <AmlMatches riskSignalId={id} />;
          $[48] = id;
          $[49] = t16;
        } else {
          t16 = $[49];
        }
        let t17;
        if ($[50] !== t14 || $[51] !== t15 || $[52] !== t16) {
          t17 = {
            type: "aml" as DialogType,
            label: t14,
            title: t15,
            content: t16
          };
          $[50] = t14;
          $[51] = t15;
          $[52] = t16;
          $[53] = t17;
        } else {
          t17 = $[53];
        }
        t13 = t17;
        break bb0;
      }
      t13 = null;
    }
    dialogConfig = t13;
    t9 = "grid grid-cols-1 sm:grid-cols-2 px-4 py-3 gap-x-5 gap-y-4 w-[540px] max-w-[calc(100vw-2rem)] max-h-[70dvh] overflow-y-auto break-words";
    t10 = attributesInDetails.map(_temp24);
    $[14] = dataSource;
    $[15] = description;
    $[16] = getVendorName;
    $[17] = id;
    $[18] = note;
    $[19] = reasonCode;
    $[20] = riskSignal.hasAmlHits;
    $[21] = riskSignal.hasSentilinkDetail;
    $[22] = sentilinkData;
    $[23] = severity;
    $[24] = severityColor;
    $[25] = t;
    $[26] = timestamp;
    $[27] = dialogConfig;
    $[28] = handleCloseDialog;
    $[29] = handleViewDetails;
    $[30] = t10;
    $[31] = t9;
  } else {
    dialogConfig = $[27];
    handleCloseDialog = $[28];
    handleViewDetails = $[29];
    t10 = $[30];
    t9 = $[31];
  }
  let t11;
  if ($[54] !== dialogConfig || $[55] !== handleViewDetails) {
    t11 = dialogConfig && <div key={dialogConfig.type} className="flex justify-start sm:col-span-2 pt-1"><Button variant="secondary" onClick={() => handleViewDetails(dialogConfig.type)} fullWidth={true}>{dialogConfig.label}</Button></div>;
    $[54] = dialogConfig;
    $[55] = handleViewDetails;
    $[56] = t11;
  } else {
    t11 = $[56];
  }
  let t12;
  if ($[57] !== t10 || $[58] !== t11 || $[59] !== t9) {
    t12 = <div className={t9}>{t10}{t11}</div>;
    $[57] = t10;
    $[58] = t11;
    $[59] = t9;
    $[60] = t12;
  } else {
    t12 = $[60];
  }
  const PopoverContent = t12;
  let t13;
  if ($[61] !== reasonCode) {
    t13 = <span className="text-snippet-1 text-secondary group-hover:text-primary wrap-anywhere md:wrap-normal">{reasonCode}</span>;
    $[61] = reasonCode;
    $[62] = t13;
  } else {
    t13 = $[62];
  }
  let t14;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Divider variant="secondary" className="mb-1" />;
    $[63] = t14;
  } else {
    t14 = $[63];
  }
  const t15 = `text-label-2 text-${severityColor}`;
  let t16;
  if ($[64] !== riskSignalSeverityT || $[65] !== severity) {
    t16 = riskSignalSeverityT(severity);
    $[64] = riskSignalSeverityT;
    $[65] = severity;
    $[66] = t16;
  } else {
    t16 = $[66];
  }
  let t17;
  if ($[67] !== t15 || $[68] !== t16) {
    t17 = <span className={t15}>{t16}</span>;
    $[67] = t15;
    $[68] = t16;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  let t18;
  if ($[70] !== t13 || $[71] !== t17) {
    t18 = <div className="relative flex items-end gap-2 px-2 py-1 -mx-2 transition-colors rounded-sm cursor-pointer duration-50 z-1 isolate hover:bg-hover group">{t13}{t14}{t17}</div>;
    $[70] = t13;
    $[71] = t17;
    $[72] = t18;
  } else {
    t18 = $[72];
  }
  let t19;
  if ($[73] !== PopoverContent || $[74] !== isPopoverOpen || $[75] !== t18) {
    t19 = <Popover content={PopoverContent} onEscapeKeyDown={handleEscapeKeyDown} onPointerDownOutside={handlePointerDownOutside} controlledOpen={isPopoverOpen} onControlledOpenChange={setIsPopoverOpen}>{t18}</Popover>;
    $[73] = PopoverContent;
    $[74] = isPopoverOpen;
    $[75] = t18;
    $[76] = t19;
  } else {
    t19 = $[76];
  }
  let t20;
  if ($[77] !== activeDialog || $[78] !== dialogConfig || $[79] !== handleCloseDialog) {
    t20 = activeDialog && dialogConfig && <Dialog title={dialogConfig.title} open={true} onClose={handleCloseDialog}>{dialogConfig.content}</Dialog>;
    $[77] = activeDialog;
    $[78] = dialogConfig;
    $[79] = handleCloseDialog;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  let t21;
  if ($[81] !== t19 || $[82] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[81] = t19;
    $[82] = t20;
    $[83] = t21;
  } else {
    t21 = $[83];
  }
  return t21;
};
export default RiskSignalItem;
function _temp(severity_0) {
  const severityMap = {
    high: "error",
    medium: "warning",
    low: "info",
    info: "neutral"
  };
  return severityMap[severity_0] || "neutral";
}
function _temp2($) {
  return $.vendor.footprint;
}
function _temp3($_0) {
  return $_0.vendor.idology;
}
function _temp4($_1) {
  return $_1.vendor.lexis;
}
function _temp5($_2) {
  return $_2.vendor.experian;
}
function _temp6($_3) {
  return $_3.vendor.twilio;
}
function _temp7($_4) {
  return $_4.vendor.middesk;
}
function _temp8($_5) {
  return $_5.vendor.incode;
}
function _temp9($_6) {
  return $_6.vendor.stytch;
}
function _temp10($_7) {
  return $_7.vendor.neuro_id;
}
function _temp11($_8) {
  return $_8.vendor.samba_safety;
}
function _temp12($_9) {
  return $_9.vendor.sentilink;
}
function _temp13($_10) {
  return $_10.vendor.comply_advantage;
}
function _temp14($_11) {
  return $_11.details.description;
}
function _temp15($_12) {
  return $_12.details.note;
}
function _temp16($_13) {
  return $_13.details.dataSource;
}
function _temp17($_14) {
  return $_14.details.reasonCode;
}
function _temp18($_15) {
  return $_15.details.severity;
}
function _temp19($_16) {
  return $_16.details.timestamp;
}
function _temp20($_17) {
  return $_17.details.viewSignalScores;
}
function _temp21($_18) {
  return $_18.dialogs.sentilink;
}
function _temp22($_19) {
  return $_19.details.viewMatches;
}
function _temp23($_20) {
  return $_20.dialogs.amlMedia;
}
function _temp24(t0) {
  const {
    label,
    value,
    className
  } = t0;
  return <span key={label} className={cx("text-label-2 text-secondary flex flex-col gap-0.5", className)}><p className="text-label-2 text-tertiary">{label}</p><p className={cx(className || "text-primary")}>{value}</p></span>;
}